import {
    Box,
    Button,
    Grid,
    IconButton,
    makeStyles,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@material-ui/core";
import {
    ToggleButton,
    ToggleButtonGroup
} from "@material-ui/lab";
import React from "react";
import {
    Controller,
    useFieldArray,
    useForm
} from "react-hook-form";
import {useLoading} from "../../../../util/LoadingProvider";
import {Delete} from "@material-ui/icons";
import AddCircleIcon from "@material-ui/icons/AddCircle";
import {modal} from "../../../../util";
import apiHelper from "../../../../util/ApiHelper";
import {toast} from "react-toastify";
import * as yup from "yup";
import {yupResolver} from "@hookform/resolvers/yup";

const schema = yup.object().shape({
    localizations: yup.object().shape({
        en: yup.object().shape({
            name: yup.string().required("Name is required"),
        }),
        tc: yup.object().shape({
            name: yup.string().required("Name is required"),
        }),
    }),
});

const useStyles = makeStyles((theme) => ({
    innerBox: {
        background: "#ffffff",
        marginBottom: theme.spacing(4),
        "& *": {
            fontSize: "13px",
            lineHeight: "15px",
        },
    },
    sectionBox: {
        paddingTop: theme.spacing(3),
        paddingBottom: theme.spacing(3),
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(3),
    },
    sectionTitle: {
        fontWeight: "300",
        fontSize: "13px",
        lineHeight: "15px",
        color: "#333333",
    },
    itemTitle: {
        fontWeight: "bold",
        fontSize: "12px",
        lineHeight: "14px",
        color: "#333333",
    },
    itemValue: {
        fontWeight: "normal",
        fontSize: "14px",
        lineHeight: "16px",
        color: "#333333",
    },
    input: {
        width: "100%",
        "& button": {
            fontSize: "13px",
            lineHeight: "15px",
            width: "25%",
        },
    },
    buttonGroup: {
        textAlign: "right",
        marginTop: "-20px",
        "& button": {
            color: "#306F9D",
            fontSize: "13px",
            textTransform: "inherit",
        },
    },
    customLabel: {
        margin: "0 !important",
        "& label": {
            color: "black",
            fontWeight: 700,
            fontSize: "16px",
        },
        "& p": {
            fontSize: "12px",
        },
    },
    addBtn: {
        color: "#306F9D",
        margin: 5,
    },
    delBtn: {
        color: "#C85250",
        margin: 5,
    },
}));

const defaultFormValues = {
    id: null,
    localizations: {
        en: {
            name: "",
        },
        tc: {
            name: "",
        },
    },
    status: 'ACTIVE',
    price: 0,
    detail: []
};

function MemberPlanDetailPage({disabled, ...props}) {
    const classes = useStyles();
    const {match, history} = props;
    const {id} = match.params;
    const [isEditable, setIsEditable] = React.useState(true);
    const [paramId, setParamId] = React.useState(id);
    const [defaultValue, setDefaultValue] = React.useState(defaultFormValues);
    const {setLoading} = useLoading();

    const {
        control,
        handleSubmit,
        getValues,
        setValue,
        reset,
    } = useForm({
        defaultValues: React.useMemo(() => defaultValue, [defaultValue]),
        resolver: yupResolver(schema),
    });

    const {fields, append, remove} = useFieldArray({
        control,
        name: "detail"
    });

    const onSubmit = async (value) => {
        setLoading(true);

        try {
            if (paramId !== "new") {
                const result = await apiHelper.hkiff.updatePlanById(paramId, value);
                if (result.status === 200) {
                    toast.dark("Plan detail is updated.");
                }
            } else {
                const result = await apiHelper.hkiff.createPlan(value);
                if (result.status === 200) {
                    setParamId(result.data.id);
                    history.push(`/hkiff/membership/plan/${result.data.id}`);
                    toast.dark("Plan detail is created.");
                }
            }
        } catch (e) {
            console.log(e);
        }
        setLoading(false);
    };

    const deletePlanDetail = async (index) => {
        const decision = await modal.fire({
            title: "Delete Plan Detail",
            text: "Are you sure to delete this plan detail?",
            showCancelButton: true,
            confirmButtonText: `Delete`,
        });
        if (decision.isConfirmed) {
            remove(index);
        }
    };

    const getMemberPlanDetailById = React.useCallback(async () => {
        try {
            setLoading(true);
            const result = await apiHelper.hkiff.getPlanById(id);
            const data = result.data;
            if (data?.editable === false) {
                setIsEditable(false);
                toast.dark('This plan is in use. Therefore, some fields are not editable now.', {
                    autoClose: 6000,
                    pauseOnHover: false,
                });
            }
            setDefaultValue(data);
            reset(data);
        } catch (err) {
            console.log(err);
        } finally {
            setLoading(false);
        }
    }, [id, reset, setLoading]);

    React.useEffect(() => {
        if (id !== "new") getMemberPlanDetailById();
    }, [getMemberPlanDetailById, id]);

    return (
        <Box>
            <Typography variant="h4" gutterBottom>
                {`Membership > ${id === "new" ? "Add" : "Edit"} Plan Detail`}
            </Typography>
            <form onSubmit={handleSubmit(onSubmit)}>
                <fieldset disabled={disabled}
                          className="fieldset">
                    <Box className={classes.innerBox}>
                        <Box className={classes.sectionBox}>
                            <Grid container xs={12}
                                  direction="row"
                                  spacing={3}>
                                <Grid item xs={12}>
                                    <strong>Plan</strong>
                                </Grid>
                            </Grid>
                            <Grid container xs={12}
                                  direction="row"
                                  spacing={3}>
                                <Grid item xs={12}>
                                    <Controller
                                        name="localizations.en.name"
                                        control={control}
                                        render={({
                                                     field,
                                                     fieldState: {error}
                                                 }) => (
                                            <TextField
                                                label="Plan Name (English)"
                                                variant="outlined"
                                                margin="dense"
                                                className={classes.textInput}
                                                fullWidth
                                                {...field}
                                                error={!!error}
                                                helperText={error ? error.message : ""}
                                                disabled={!isEditable}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Controller
                                        name="localizations.tc.name"
                                        control={control}
                                        render={({
                                                     field,
                                                     fieldState: {error}
                                                 }) => (
                                            <TextField
                                                label="Plan Name (Traditional Chinese)"
                                                variant="outlined"
                                                margin="dense"
                                                className={classes.textInput}
                                                fullWidth
                                                {...field}
                                                error={!!error}
                                                helperText={error ? error.message : ""}
                                                disabled={!isEditable}
                                            />
                                        )}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <Controller
                                        name="price"
                                        control={control}
                                        render={({
                                                     field: {
                                                         value,
                                                         ...rest
                                                     },
                                                     fieldState: {error},
                                                 }) => (
                                            <TextField
                                                label="Price"
                                                variant="outlined"
                                                margin="dense"
                                                className={classes.textInput}
                                                type="number"
                                                InputProps={{
                                                    inputProps: {
                                                        min: 0,
                                                    }
                                                }}
                                                fullWidth
                                                {...rest}
                                                value={value}
                                                error={!!error}
                                                helperText={error ? error.message : ""}
                                                onWheel={(e) => e.target.blur()}
                                                disabled={!isEditable}
                                            />
                                        )}
                                    />
                                </Grid>

                                <Grid container item xs={12}
                                      spacing={3}>
                                    <Grid item xs={4}>
                                        <Controller
                                            control={control}
                                            name="status"
                                            render={({
                                                         field: {
                                                             value,
                                                             onChange,
                                                             ...rest
                                                         },
                                                         fieldState: {error},
                                                     }) => (
                                                <ToggleButtonGroup
                                                    {...rest}
                                                    value={value}
                                                    onChange={(event, newFormats) => {
                                                        if (newFormats) {
                                                            onChange(newFormats);
                                                        }
                                                    }}
                                                    exclusive
                                                    aria-label="Status"
                                                    className={classes.input}
                                                >
                                                    <ToggleButton
                                                        value={'ACTIVE'}
                                                        aria-label="Pending"
                                                        style={{width: "50%"}}
                                                    >
                                                        Active
                                                    </ToggleButton>
                                                    <ToggleButton
                                                        value={'INACTIVE'}
                                                        aria-label="Confirmed"
                                                        style={{width: "50%"}}
                                                    >
                                                        Inactive
                                                    </ToggleButton>
                                                </ToggleButtonGroup>
                                            )}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Box>
                    </Box>

                    <Box className={classes.innerBox}>
                        <Box className={classes.sectionBox}>
                            <Grid container xs={12}
                                  direction="row"
                                  spacing={3}>
                                <Grid item xs={12}>
                                    <strong>Plan
                                        detail</strong>
                                </Grid>
                            </Grid>
                            <Box mt={2}>
                                <Grid container xs={12}
                                      direction="row"
                                      spacing={1}>
                                    <TableContainer
                                        component={Paper}>
                                        <Table
                                            className={classes.table}
                                            aria-label="plan detail table">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Plan
                                                        Detail
                                                        (English)</TableCell>
                                                    <TableCell>Plan
                                                        Detail
                                                        (Traditional
                                                        Chinese)</TableCell>
                                                    <TableCell>
                                                        <IconButton
                                                            className={classes.addBtn}
                                                            size="small"
                                                            disabled={!isEditable || disabled}
                                                            onClick={() => {
                                                                append({
                                                                    id: 0,
                                                                    localizations: {
                                                                        en: {
                                                                            value: ''
                                                                        },
                                                                        tc: {
                                                                            value: ''
                                                                        }
                                                                    }
                                                                })
                                                            }}
                                                        >
                                                            <AddCircleIcon/>
                                                            <span>Click here to add plan detail</span>
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {
                                                    fields.map((item, index) => {
                                                        return (
                                                            <TableRow>
                                                                <TableCell>
                                                                    <Controller
                                                                        defaultValue={fields[index] && fields[index].localizations && fields[index].localizations.en.value}
                                                                        name={`detail.${index}.localizations.en.value`}
                                                                        control={control}
                                                                        rules={{required: "Required"}}
                                                                        render={({
                                                                                     field,
                                                                                     fieldState: {error}
                                                                                 }) => (
                                                                            <TextField
                                                                                margin="dense"
                                                                                className={classes.textInput}
                                                                                fullWidth
                                                                                error={!!error}
                                                                                label="Plan Detail (English)"
                                                                                helperText={error ? error.message : ""}
                                                                                {...field}
                                                                                disabled={!isEditable}
                                                                            />
                                                                        )}
                                                                    />
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Controller
                                                                        defaultValue={fields[index] && fields[index].localizations && fields[index].localizations.tc.value}
                                                                        name={`detail.${index}.localizations.tc.value`}
                                                                        control={control}
                                                                        rules={{required: "Required"}}
                                                                        render={({
                                                                                     field,
                                                                                     fieldState: {error}
                                                                                 }) => (
                                                                            <TextField
                                                                                margin="dense"
                                                                                className={classes.textInput}
                                                                                fullWidth
                                                                                error={!!error}
                                                                                label="Plan Detail (Traditional Chinese)"
                                                                                helperText={error ? error.message : ""}
                                                                                {...field}
                                                                                disabled={!isEditable}
                                                                            />
                                                                        )}
                                                                    />
                                                                </TableCell>
                                                                <TableCell>
                                                                    <IconButton
                                                                        className={classes.delBtn}
                                                                        size="small"
                                                                        disabled={disabled || !isEditable}
                                                                        onClick={() => {
                                                                            deletePlanDetail(index);
                                                                        }}
                                                                    >
                                                                        <Delete
                                                                            fontSize="inherit"/>
                                                                        <span>Delete</span>
                                                                    </IconButton>
                                                                </TableCell>
                                                            </TableRow>
                                                        )
                                                    })
                                                }
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Grid>
                            </Box>
                        </Box>
                    </Box>

                    <div className={classes.buttonGroup}>
                        {disabled || (
                            <div
                                className={classes.buttonGroup}>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    disableElevation
                                    style={{
                                        color: "white",
                                        background: "#306F9D",
                                        marginRight: "5px",
                                        padding: "9px 16px",
                                    }}
                                >
                                    Save Changes
                                </Button>
                            </div>
                        )}
                    </div>
                </fieldset>
            </form>
        </Box>
    );
}

export default MemberPlanDetailPage;
